import type { ReactNode } from 'react';
import type { EditProps, ViewProps } from './types.ts';

/** Minimal inline markdown: **bold** and *italic*. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let last = 0;
  let i = 0;
  for (const match of text.matchAll(re)) {
    const idx = match.index;
    if (idx > last) out.push(text.slice(last, idx));
    const token = match[0];
    if (token.startsWith('**')) {
      out.push(<strong key={`${keyPrefix}-${i++}`}>{token.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={`${keyPrefix}-${i++}`}>{token.slice(1, -1)}</em>);
    }
    last = idx + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function NarrativeView({ content }: ViewProps<'narrative'>) {
  const text = content.text.trim();
  if (!text) return null;
  const paragraphs = text.split(/\n\s*\n/);
  return (
    <div className="space-y-3 text-sm leading-relaxed text-gray-700">
      {paragraphs.map((para, pi) => {
        const lines = para.split('\n');
        const bullets = lines.filter((l) => l.trimStart().startsWith('- '));
        if (bullets.length > 0 && bullets.length === lines.length) {
          return (
            <ul key={pi} className="list-disc space-y-1 pl-5">
              {lines.map((l, li) => (
                <li key={li}>{inline(l.trimStart().slice(2), `${pi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        return <p key={pi}>{inline(para, `${pi}`)}</p>;
      })}
    </div>
  );
}

export function NarrativeEdit({ content, onChange }: EditProps<'narrative'>) {
  return (
    <textarea
      className="min-h-40 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
      value={content.text}
      placeholder="Write prose here. Blank line = new paragraph; lines starting with '- ' become bullets; **bold** and *italic* supported."
      onChange={(e) => onChange({ ...content, text: e.target.value })}
    />
  );
}
